import { EventType } from "@/analytics/analytics-events";
import { EventBarrier } from "@/components/common/event-barrier";
import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { FaroText, grey } from "@faro-lotv/flat-ui";
import { Analytics, NO_TRANSLATE_CLASS } from "@faro-lotv/foreign-observers";
import { alpha } from "@mui/system";
import { debounce } from "es-toolkit";
import { useState, type JSX } from "react";
import type { Vector3Tuple } from "three";

/**
 * Minimum time between two tracked hover events on the same label, used to
 * throttle rapid pointer re-entries.
 */
const HOVER_TRACK_COOLDOWN_MS = 2000;

type BoundingBoxLabelProps = {
  /** Local-space position of the label anchor on the unit sphere. */
  position: Vector3Tuple;
  /** The text to display in the label. */
  label: string;
  /** Color of the finding, to tell the label apart from the ones around it. */
  color?: string;
  /** Called when the label is clicked. */
  onClick?(): void;
  /** Adjusting style and user interaction based on user permissions */
  allowAnnotationCreation: boolean;
};

/**
 * @returns the clickable HTML label anchored to the top-left corner of a
 * bounding box overlay.
 */
export function BoundingBoxLabel({
  position,
  label,
  color,
  onClick,
  allowAnnotationCreation,
}: BoundingBoxLabelProps): JSX.Element {
  const [shouldElide, setsShouldElide] = useState(true);

  // Debounce on the leading edge so the event fires immediately on the first
  // hover but suppresses rapid pointer re-entries (e.g. the user moving the
  // mouse back and forth). Genuine re-hovers after the cooldown are tracked.
  // useState (lazy init) guarantees a single stable instance for the lifetime
  // of the component, unlike useMemo which may discard and recompute its value.
  const [trackHover] = useState(() =>
    debounce(
      () => Analytics.track(EventType.hoverOverAiAnnotationTitle),
      HOVER_TRACK_COOLDOWN_MS,
      { edges: ["leading"] },
    ),
  );

  return (
    <AppAwareHtml position={position}>
      <EventBarrier>
        <FaroText
          onPointerEnter={() => {
            trackHover();
            setsShouldElide(false);
          }}
          onPointerLeave={() => setsShouldElide(true)}
          dark
          className={NO_TRANSLATE_CLASS}
          variant="bodyM"
          shouldElide={shouldElide}
          sx={{
            display: "block",

            py: 0.75,
            px: 1.5,

            backgroundColor: grey[1000],
            outline: `1px solid ${alpha(color ?? grey[0], color ? 0.5 : 0.1)}`,
            borderRadius: 1.5,

            width: "200px",
            cursor: allowAnnotationCreation ? "pointer" : "default",
            userSelect: allowAnnotationCreation ? undefined : "none",
          }}
          onClick={onClick}
        >
          {label}
        </FaroText>
      </EventBarrier>
    </AppAwareHtml>
  );
}
